import { useContext, useEffect, useState } from 'react';
import apiUser from '../../apiUser';
import { UserContext } from '../../contexts/userProvider';
import { avisoDe, fmtDataHora } from './format';

// Cor da barra lateral por tipo de item da timeline (comentário interno,
// contestação do cliente e a resposta interna a ela, evento do próprio ciclo).
const CORES = {
    comentario: 'border-vox-blue-500',
    contestacao: 'border-amber-500',
    contestacao_resposta: 'border-amber-300',
    evento: 'border-gray-300 dark:border-gray-600',
};

// Timeline do ciclo (comentários internos + contestações + eventos) com caixa de
// comentário abaixo. Renderizado como filho do Modal do módulo (ver Modal.jsx) —
// por isso NÃO tem overflow/scroll próprio: o único container de rolagem é o do
// Modal, que engloba a lista e a caixa de comentário juntas.
//
// Chave da lista: `${tipo}-${id}` (índice quando id ausente) — `id` NÃO é único
// entre tipos: contestacao e contestacao_resposta compartilham o mesmo id, e
// eventos podem não ter id.
const TimelineCiclo = ({ idCiclo }) => {
    const { user, isAdmin, setAlert } = useContext(UserContext);
    const aviso = avisoDe(setAlert);
    const [itens, setItens] = useState([]);
    const [mensagem, setMensagem] = useState('');
    const [carregando, setCarregando] = useState(true);

    const carregar = async () => {
        try {
            const { data } = await apiUser.get('/faturamento/ciclo/timeline', { params: { id_ciclo: idCiclo } });
            if (data.success) setItens(data.dados || []);
            else aviso(data.message || 'Erro ao carregar o histórico.');
        } catch {
            aviso('Erro ao carregar o histórico.');
        } finally {
            setCarregando(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); }, [idCiclo]);

    const comentar = async () => {
        try {
            const { data } = await apiUser.post('/faturamento/comentarios', { id_ciclo: idCiclo, mensagem });
            if (data.success) { setMensagem(''); carregar(); }
            else aviso(data.message);
        } catch {
            aviso('Erro ao comunicar com o servidor.');
        }
    };

    const remover = async (id) => {
        if (!window.confirm('Remover este comentário?')) return;
        try {
            const { data } = await apiUser.post('/faturamento/comentarios/remover', { id });
            if (data.success) carregar();
            else aviso(data.message);
        } catch {
            aviso('Erro ao comunicar com o servidor.');
        }
    };

    return (
        <div className='px-6 py-5 flex flex-col gap-2'>
            {carregando ? (
                <p className='text-sm text-gray-500 dark:text-gray-400 p-2'>Carregando…</p>
            ) : !itens.length ? (
                <p className='text-sm text-gray-500 dark:text-gray-400 p-2'>Sem histórico ainda.</p>
            ) : itens.map((it, i) => (
                <div key={`${it.tipo}-${it.id ?? i}`} className={`border-l-4 ${CORES[it.tipo]} pl-3 py-1`}>
                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                        {it.autor} · {fmtDataHora(it.data)}
                        {it.tipo === 'contestacao' && ' · contestação do cliente'}
                        {it.tipo === 'contestacao_resposta' && ' · resposta à contestação'}
                        {it.tipo === 'comentario' && (it.user_id === user?.id || isAdmin) && (
                            <button className='ml-2 text-red-500 hover:underline' onClick={() => remover(it.id)}>excluir</button>
                        )}
                    </div>
                    <div className={`text-sm ${it.tipo === 'evento' ? 'text-gray-500 dark:text-gray-400 italic' : 'text-gray-800 dark:text-gray-200'}`}>
                        {it.texto}
                    </div>
                </div>
            ))}
            <div className='border-t border-gray-200 dark:border-gray-700 pt-2 flex gap-2'>
                <textarea
                    className='flex-1 border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-sm dark:bg-gray-700 dark:text-white'
                    rows={2} maxLength={5000}
                    placeholder='Comentar (qualquer status do ciclo)…'
                    value={mensagem} onChange={(e) => setMensagem(e.target.value)}
                />
                <button
                    className='px-3 rounded-lg text-white font-semibold bg-vox-blue-600 hover:bg-vox-blue-700 disabled:opacity-50'
                    disabled={!mensagem.trim()} onClick={comentar}>
                    Enviar
                </button>
            </div>
            <span className='text-xs text-gray-400 text-right'>{mensagem.length}/5000</span>
        </div>
    );
};

export default TimelineCiclo;
